import { useCallback, useMemo } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import type { AnalyticsCardHeader } from '../analyticsModule/components/AnalyticsCard/AnalyticsCard.types'
import type { AnalyticsTableColumn } from '../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'

export const useReportSeries = (columns: AnalyticsTableColumn[], defaultSeries?: string) => {
  const [seriesParam, setSeriesParam] = useQueryParam('series', withDefault(StringParam, undefined))

  const availableColumns = useMemo(() => {
    return columns.filter(
      (column) => ['money', 'number', 'percentage'].includes(column.type) && !column.hidden,
    )
  }, [columns])

  const availableSeries = useMemo(() => {
    return availableColumns.map((column) => column.id)
  }, [availableColumns])

  const activeSeries = useMemo(() => {
    if (seriesParam === undefined) {
      return defaultSeries
    }

    const isValidSeries = availableSeries.includes(seriesParam)

    if (isValidSeries) {
      return seriesParam
    }

    setSeriesParam(undefined)

    return defaultSeries
  }, [seriesParam, availableSeries, setSeriesParam, defaultSeries])

  const setActiveSeries = useCallback(
    (newSeries: string | undefined) => {
      if (newSeries === undefined) {
        setSeriesParam(undefined)

        return
      }

      if (availableSeries.includes(newSeries)) {
        setSeriesParam(newSeries)
      }
    },
    [setSeriesParam, availableSeries],
  )

  const headerOptions = useMemo((): AnalyticsCardHeader['options'] => {
    return availableColumns
      .map((column) => {
        if (activeSeries === column.id) {
          return
        }

        return {
          value: column.id,
          label: String(column.label),
        }
      })
      .filter(Boolean) as AnalyticsCardHeader['options']
  }, [availableColumns, activeSeries])

  return {
    activeSeries,
    setActiveSeries,
    headerOptions,
  }
}
